import React, { FC, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/react-hooks';
import AddIcon from '@material-ui/icons/Add';
import { Controller, ControllerRenderProps, useForm, useFormState } from 'react-hook-form';

import * as actions from 'actions';
import FieldSelectList from 'Components-redesign/Fields/FieldSelectList';
import LangInputFields from 'Components-redesign/Fields/LangInputFields';
import { Input } from 'Components-redesign/Fields/TextInput/styles';
import Field from 'Components-redesign/Form/Field';
import { AddButton } from 'Components-redesign/Form/styles';
import SubmitButton from 'Components-redesign/Form/SubmitButton';
import QuestionModal from 'Components-redesign/QuestionModal';
import Toggle from 'Components-redesign/Toggle';
import { useDispatch, useI18n, useSelector } from 'Hooks';
import { translations } from 'i18n';
import { getCompany_company_variations as Variation } from 'Scenes-redesign/PIM/__queries__';
import { getLangText } from 'utils/langText';

import {
  CreateModificationGroup,
  UpdateModificationGroup,
  getModificationGroup,
} from './__queries__';
import {
  CREATE_MODIFICATION_GROUP,
  MODIFICATION_GROUP_QUERY,
  UPDATE_MODIFICATION_GROUP,
} from './queries';
import { InputWrapper, Wrapper } from './styles';
import { MODIFICATION_GROUP_TYPE } from '../../../../../__queries__/globalTypes';
import { getPimData_company_modificationGroups as ModificationGroup } from '../../__queries__';

/* This is the values that we gather from inputs, toggles is in a seperate state */
type BaseFormInputs = {
  internalName: string;
  variations: Variation[];
  preselected: string[];
};

const defaultValues: BaseFormInputs = {
  internalName: '',
  variations: [],
  preselected: [],
};

interface Props {
  idToUpdate?: string;
  variations: Variation[];
  modificationGroups?: ModificationGroup[];
  onFormSuccess: (node) => void;
  setShowUnsavedChangesQuestionModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ModificationGroupForm: FC<Props> = ({
  idToUpdate,
  variations,
  modificationGroups,
  onFormSuccess,
  setShowUnsavedChangesQuestionModal,
}) => {
  const { t } = useI18n();

  const dispatch = useDispatch();
  const companyData = useSelector((state) => state.companyData);
  const languages = useSelector(({ languages }) => languages);

  const [sideModalOpen, setSideModalOpen] = useState<boolean>(false);
  const [showQuestionModal, setShowQuestionModal] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [externalName, setExternalName] = useState<any>(null);
  const [expandedByDefault, setExpandedByDefault] = useState<boolean>(false);
  const [type, setType] = useState<MODIFICATION_GROUP_TYPE>(MODIFICATION_GROUP_TYPE.MULTIPLE);
  const [required, setRequired] = useState<boolean>(false);

  const { watch, reset, handleSubmit, register, control } = useForm<BaseFormInputs>({
    defaultValues,
  });

  const { isDirty } = useFormState({
    control,
  });

  const watchInternalName = watch('internalName');
  const watchVariations = watch('variations');
  const watchPreselected = watch('preselected');

  const id = idToUpdate?.replace('modification-group-', '');

  // Comment this out currently because we dont have support in backend for fetching this as SuperAdmin
  //   const query = useQuery<getModificationGroup>(MODIFICATION_GROUP_QUERY, {
  //     variables: {
  //       id: id,
  //     },
  //     skip: !id,
  //     fetchPolicy: 'no-cache',
  //   });

  //   useEffect(() => {
  //     query.refetch();
  //   }, [idToUpdate]);

  const modificationGroup = modificationGroups?.find((group) => group.id === id);

  useEffect(() => {
    if (modificationGroup) {
      reset({
        internalName: modificationGroup.internalName ?? '',
        variations: modificationGroup.variations ?? [],
        preselected: modificationGroup.preselectedVariations ?? [],
      });

      setExternalName(modificationGroup.externalName);
      setType(modificationGroup.type);
      setExpandedByDefault(modificationGroup.expandedByDefault);
      setRequired(modificationGroup.required);
    }
  }, [modificationGroups]);

  const [create] = useMutation<CreateModificationGroup>(CREATE_MODIFICATION_GROUP, {
    onCompleted: (node) => {
      dispatch(
        actions.showNotice(
          `${t(externalName[companyData?.defaultLanguage ?? 'en'])} ${t('General.WasCreated')}`,
          4000,
        ),
      );
      setLoading(false);
      onFormSuccess && onFormSuccess(node);
      reset(defaultValues);
    },
  });

  const [update] = useMutation<UpdateModificationGroup>(UPDATE_MODIFICATION_GROUP, {
    onCompleted: (node) => {
      dispatch(
        actions.showNotice(
          `${t(externalName[companyData?.defaultLanguage ?? 'en'])} ${t('General.WasUpdated')}`,
          4000,
        ),
      );
      setLoading(false);
      onFormSuccess && onFormSuccess(node);
      reset(defaultValues);
    },
  });

  const onSubmit = () => {
    if (externalName === null) return;

    if (!companyData?.id) return;

    setLoading(true);

    const data = {
      internalName:
        watchInternalName !== ''
          ? watchInternalName
          : externalName[companyData?.defaultLanguage ?? 'en'],
      externalName: {
        en: externalName?.en ?? '',
        se: externalName?.se ?? '',
        es: externalName?.es ?? '',
        no: externalName?.no ?? '',
      },
      type,
      expandedByDefault,
      required,
      preselectedVariations:
        modificationGroup?.type === MODIFICATION_GROUP_TYPE.SINGLE ? watchPreselected : [],
      variations: watchVariations.map((v) => v.id),
    };

    if (id) {
      // Update
      update({
        variables: {
          input: { id, ...data },
        },
      });
    } else {
      // Create
      create({
        variables: {
          input: { ...data },
        },
      });
    }
  };

  const handleSelect = (value) => {
    // Currently only handles 1 selected value, but prepared to handle more in the future
    if (watchPreselected.includes(value)) {
      reset({
        internalName: watchInternalName,
        variations: watchVariations,
        preselected: [],
      });
    } else {
      reset({
        internalName: watchInternalName,
        variations: watchVariations,
        preselected: [value],
      });
    }
  };

  const handleOrderChange = (
    value: string[],
    field: ControllerRenderProps<BaseFormInputs, 'variations'>,
  ) => {
    const reorderedVariations = [...watchVariations].sort(
      (a, b) => value.indexOf(a.id) - value.indexOf(b.id),
    );

    field.onChange(reorderedVariations);
  };

  const buttonText = idToUpdate
    ? t('PIM.Modifications.UpdateGroup')
    : t('PIM.Modifications.CreateGroup');

  useEffect(() => {
    if (idToUpdate && setShowUnsavedChangesQuestionModal) {
      setShowUnsavedChangesQuestionModal(isDirty);
    }
  }, [isDirty]);

  return (
    <Wrapper>
      <InputWrapper>
        <Field label={t('PIM.Modifications.InternalName')}>
          <Input
            placeholder={t('PIM.Modifications.InternalName.Desc')}
            type="text"
            {...register('internalName')}
          />
        </Field>

        <Field label={t('PIM.Modifications.ExternalName')} mandatory>
          <LangInputFields
            languages={languages}
            name="externalName"
            placeholder={languages.reduce(
              (obj, lang) => (
                (obj[lang] = translations['PIM.Modifications.ExternalName.Desc'][lang]), obj
              ),
              {},
            )}
            value={externalName}
            handleChange={(name, val, lang) =>
              setExternalName((prev) => ({ ...prev, [lang]: val }))
            }
          />
        </Field>

        <Field label={t('PIM.Modifications.ExpandedByDefault.Desc')}>
          <Toggle
            title={t('PIM.Modifications.ExpandedByDefault')}
            value={expandedByDefault}
            handleChange={() => setExpandedByDefault((prev) => !prev)}
          />
        </Field>

        <Field label={t('PIM.Modifications.Type.Desc')}>
          <Toggle
            title={t('PIM.Modifications.Type')}
            value={type === MODIFICATION_GROUP_TYPE.SINGLE ?? false}
            handleChange={(active) =>
              setType(active ? MODIFICATION_GROUP_TYPE.SINGLE : MODIFICATION_GROUP_TYPE.MULTIPLE)
            }
          />
        </Field>

        <Field label={t('PIM.Required.Description')}>
          <Toggle
            title={t('PIM.Required')}
            value={required}
            handleChange={() => setRequired((prev) => !prev)}
          />
        </Field>
      </InputWrapper>

      <Controller
        control={control}
        name="variations"
        render={({ field }) => (
          <FieldSelectList
            idToUpdate={idToUpdate}
            formId={'variations'}
            values={watchVariations}
            {...field}
            list={variations}
            handleChange={(values) => field.onChange(values)}
            isModalOpen={sideModalOpen}
            selectedValues={watchPreselected}
            handleSelectValues={
              type === MODIFICATION_GROUP_TYPE.SINGLE ? (value) => handleSelect(value) : undefined
            } // Only show select if its single
            handleOrderChange={(value) => handleOrderChange(value, field)}
          />
        )}
      />

      <AddButton
        onClick={() => {
          setSideModalOpen(true);
          setTimeout(() => {
            setSideModalOpen(false);
          }, 300);
        }}>
        {t('PIM.AddIngredients')}
        <AddIcon htmlColor="#1642be" />
      </AddButton>
      <SubmitButton
        extraSpaceLeft
        disabled={loading}
        handleSubmit={handleSubmit(onSubmit)}
        floating={true}
        buttonText={loading ? `${t('General.Loading')}...` : buttonText}
      />
      {showQuestionModal && (
        <QuestionModal
          isShowing={showQuestionModal}
          question={`${t('PIM.AlreadyIngredientGroupWithName')} ${
            externalName[companyData?.defaultLanguage ?? 'en']
          }, ${t('PIM.SureWantToContinue')}`}
          onLeftClick={handleSubmit(onSubmit)}
          leftStyle="Warning"
          onRightClick={() => setShowQuestionModal(false)}
          rightStyle="OK"
        />
      )}
    </Wrapper>
  );
};

export default ModificationGroupForm;
